import React, {useEffect, useState, useCallback, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {useRoom} from '../components/RoomContext';
import {
    X,
    Users,
    User,
    Trash2,
    Plus,
    Share2,
    Check,
    Copy,
    Loader,
    AlertCircle,
    UserPlus,
    CircleDashed,
    LogOut,
    Popcorn,
    Sandwich,
    Beer,
    Soup,
    Drumstick,
    Pencil,
    ArrowUpToLine
} from 'lucide-react';
import {socket} from '../services/pocketbaseApi';
import { Dialog } from '@headlessui/react';

const apiUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

export default function Room() {
    const {roomId} = useParams();
    const {room, username, joinRoom, setUsername, leaveRoom} = useRoom();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingAdd, setPendingAdd] = useState(false);
    const [showAddToast, setShowAddToast] = useState(false);
    const [error, setError] = useState('');
    const [shareSuccess, setShareSuccess] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [roomMembers, setRoomMembers] = useState([]);
    const [showNameInput, setShowNameInput] = useState(false);
    const [nameInput, setNameInput] = useState('');

    // Modal state for friet-bestelling
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [order, setOrder] = useState({
        frieten: [],
        snacks: [],
        broodjes: [],
        dranken: [],
        andere: []
    });
    // Temp input state for each section
    const [inputs, setInputs] = useState({
        frieten: [{naam: '', aantal: 1}],
        snacks: [{naam: '', aantal: 1}],
        broodjes: [{naam: '', aantal: 1}],
        dranken: [{naam: '', aantal: 1}],
        andere: [{naam: '', aantal: 1}],
    });
    const [editingOrderId, setEditingOrderId] = useState(null);
    const [activeTab, setActiveTab] = useState('combined');

    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [leaving, setLeaving] = useState(false);

    const placeholders = {
        frieten: 'Bijv. Grote friet mayo',
        snacks: 'Bijv. Boulet',
        broodjes: 'Bijv. Martino',
        dranken: 'Bijv. Cola',
        andere: 'Iets anders'
    };

// fallback als key niet bestaat
    const getPlaceholder = key =>
        placeholders[key] || 'Bijv. Productnaam';


    // Username prompt logic: only managed here!
    useEffect(() => {
        let stored = null;
        try {
            stored = JSON.parse(localStorage.getItem(`room_${roomId}_user`));
        } catch (e) {
            stored = null;
        }
        if (!stored || !stored.username) {
            setShowNameInput(true);
            setUsername('');
        } else {
            setUsername(stored.username);
            setShowNameInput(false);
        }
        // eslint-disable-next-line
    }, [roomId]); // setUsername is stable

    // Save username to localStorage whenever it changes and is not empty
    useEffect(() => {
        if (username) {
            localStorage.setItem(`room_${roomId}_user`, JSON.stringify({username}));
        }
    }, [username, roomId]);

    // Fetch room records
    const fetchRecords = useCallback(async () => {
        if (!username) return;

        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${apiUrl}/api/room/${roomId}/bestellingen`);
            if (!res.ok) throw new Error('Failed to fetch records');
            setRecords(await res.json());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [username, roomId]);

    // Fetch room members from room_members collection
    const fetchMembers = useCallback(async () => {
        try {
            if (!username) return;
            const res = await fetch(`${apiUrl}/api/room/${roomId}/members`);
            if (!res.ok) throw new Error('Failed to fetch room members');
            const data = await res.json();
            setRoomMembers(data);

        } catch (err) {
            // Optionally handle error
        }
    }, [username, roomId]);

    // Main effect: fetch data and set up subscription
    useEffect(() => {
        let ignore = false;
        setLoading(true);

        fetchRecords().then(() => {
            if (!ignore) setLoading(false);
        });
        fetchMembers();

        // Real-time updates for bestellingen
        const event = `room_bestellingen_update_${roomId}`;
        socket.emit('join_room', roomId);
        const handler = (newRecords) => {
            if (!ignore) setRecords(newRecords);
        };
        socket.on(event, handler);
        return () => {
            ignore = true;
            socket.emit('leave_room', roomId);
            socket.off(event, handler);
        };
    }, [fetchRecords, fetchMembers, roomId]);

    // Real-time updates for room members
    useEffect(() => {
        if (!roomId) return;
        if (!username) return;
        const event = `room_members_update_${roomId}`;
        const handler = (members) => setRoomMembers(members);
        socket.on(event, handler);
        return () => socket.off(event, handler);
    }, [roomId, username]);

    // Rehydrate context if missing (room is null but username and roomId are present)
    React.useEffect(() => {
        if (!room && username && roomId) {
            fetch(`${apiUrl}/api/room/${roomId}`)
                .then(res => res.json())
                .then(roomData => {
                    if (roomData && roomData.id) {
                        joinRoom(roomData, username);
                    }
                });
        }
        // eslint-disable-next-line
    }, [room, username, roomId]);

    // Join room with name
    const handleJoinRoom = async (e) => {
        e.preventDefault();
        if (!nameInput.trim()) return;
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${apiUrl}/api/room/${roomId}/join`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({username: nameInput.trim()})
            });
            if (!res.ok) {
                let msg = 'Failed to join room';
                try {
                    const errData = await res.json();
                    msg = errData?.error || msg;
                } catch {
                }
                throw new Error(msg);
            }
            // Always fetch the latest members after join
            await fetchMembers();
            const roomData = await res.json();
            joinRoom(roomData, nameInput.trim());
            setShowNameInput(false);
            setUsername(nameInput.trim());
            setNameInput('');
            setRoomMembers(roomData.members || []);
            // localStorage is now handled by the useEffect above
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Sharing logic
    const handleShare = async () => {
        const link = `${window.location.origin}/room/${roomId}`;
        const text = `Kom samen frietjes bestellen via ${link}!`;
        try {
            if (navigator.share) {
                await navigator.share({title: 'Room Invite', text, url: link});
            } else {
                await navigator.clipboard.writeText(text);
            }
            setShareSuccess(true);
            setTimeout(() => setShareSuccess(false), 2000);
        } catch {
        }
    };

    // Copy link logic
    const handleCopy = async () => {
        const link = `${window.location.origin}/room/${roomId}`;
        const text = `${link}`;
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch {
        }
    };

    // Add this handler for leaving the room (with order deletion)
    const handleLeaveRoom = async () => {
        setLeaving(true);
        try {
            // Delete all user's orders in this room
            const myOrders = records.filter(r => r.title === username);
            for (const order of myOrders) {
                await fetch(`${apiUrl}/api/room/${roomId}/bestellingen/${order.id}`, {
                    method: 'DELETE',
                });
            }
            await leaveRoom();
            window.location.href = '/';
        } catch (err) {
            setLeaving(false);
            console.error('[Room.js] leaveRoom error', err);
        }
    };

    // Helper to handle input changes
    const handleInputChange = (section, idx, field, value) => {
        setInputs(prev => {
            const updated = [...prev[section]];
            updated[idx][field] = value;
            return {...prev, [section]: updated};
        });
    };
    // Refs for focusing new rows
    const inputRefs = useRef({
        frieten: [],
        snacks: [],
        broodjes: [],
        dranken: [],
        andere: []
    });
    const [focusRow, setFocusRow] = useState(null); // { section, idx }
    // Add new row in section
    const handleAddRow = (section) => {
        setInputs(prev => {
            const newArr = [...prev[section], {naam: '', aantal: 1}];
            setFocusRow({section, idx: newArr.length - 1});
            return {...prev, [section]: newArr};
        });
    };
    // Remove row in section
    const handleRemoveRow = (section, idx) => {
        setInputs(prev => {
            const updated = prev[section].filter((_, i) => i !== idx);
            return {...prev, [section]: updated.length ? updated : [{naam: '', aantal: 1}]};
        });
    };
    // Open modal for editing
    const handleEditOrder = (record) => {
        setEditingOrderId(record.id);
        // Prefill modal with record.order
        setInputs({
            frieten: record.order?.frieten?.length ? record.order.frieten.map(i => ({...i})) : [{naam: '', aantal: 1}],
            snacks: record.order?.snacks?.length ? record.order.snacks.map(i => ({...i})) : [{naam: '', aantal: 1}],
            broodjes: record.order?.broodjes?.length ? record.order.broodjes.map(i => ({...i})) : [{
                naam: '',
                aantal: 1
            }],
            dranken: record.order?.dranken?.length ? record.order.dranken.map(i => ({...i})) : [{naam: '', aantal: 1}],
            andere: record.order?.andere?.length ? record.order.andere.map(i => ({...i})) : [{naam: '', aantal: 1}],
        });
        setShowOrderModal(true);
    };

    // Submit order (add or edit)
    const handleOrderSubmit = async (e) => {
        e.preventDefault();
        setPendingAdd(true);
        setError('');
        // Verzamel ALLE rijen met een niet-lege naam, ongeacht positie
        // Uppercase first letter, lowercase rest for consistency
        const cleanOrder = Object.fromEntries(
            Object.entries(inputs).map(([section, arr]) => [
                section,
                arr
                    .map(i => ({
                        naam: i.naam.trim() ? i.naam.trim().charAt(0).toUpperCase() + i.naam.trim().slice(1).toLowerCase() : '',
                        aantal: Number(i.aantal) || 1
                    }))
                    .filter(i => i.naam)
            ])
        );
        try {
            let newRecord;
            if (editingOrderId) {
                // Update bestaande bestelling
                const res = await fetch(`${apiUrl}/api/room/${roomId}/bestellingen/${editingOrderId}`, {
                    method: 'PUT',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        title: username,
                        description: '',
                        room: roomId,
                        order: cleanOrder
                    })
                });
                if (!res.ok) throw new Error('Bestelling bijwerken mislukt');
                newRecord = await res.json();
                // Do NOT update setRecords here; socket will update
            } else {
                // Nieuwe bestelling
                const res = await fetch(`${apiUrl}/api/room/${roomId}/bestellingen`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        title: username,
                        description: '',
                        room: roomId,
                        order: cleanOrder
                    })
                });
                if (!res.ok) throw new Error('Bestelling toevoegen mislukt');
                newRecord = await res.json();
                // Do NOT update setRecords here; socket will update
            }
            setShowOrderModal(false);
            setShowAddToast(true);
            setTimeout(() => setShowAddToast(false), 1300);
            setInputs({
                frieten: [{naam: '', aantal: 1}],
                snacks: [{naam: '', aantal: 1}],
                broodjes: [{naam: '', aantal: 1}],
                dranken: [{naam: '', aantal: 1}],
                andere: [{naam: '', aantal: 1}],
            });
            setEditingOrderId(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setPendingAdd(false);
        }
    };

    // Helper: combine all orders by category and item name
    const combinedOrders = React.useMemo(() => {
        const categories = ['frieten', 'snacks', 'broodjes', 'dranken', 'andere'];
        const combined = {};
        for (const cat of categories) combined[cat] = {};
        records.forEach(record => {
            if (!record.order) return;
            for (const cat of categories) {
                (record.order[cat] || []).forEach(item => {
                    if (!item.naam) return;
                    if (!combined[cat][item.naam]) {
                        combined[cat][item.naam] = 0;
                    }
                    combined[cat][item.naam] += Number(item.aantal) || 1;
                });
            }
        });
        return combined;
    }, [records]);

    // Focus effect for new row
    useEffect(() => {
        if (focusRow && inputRefs.current[focusRow.section][focusRow.idx]) {
            inputRefs.current[focusRow.section][focusRow.idx].focus();
            setFocusRow(null);
        }
    }, [inputs, focusRow]);

    // Gather unique order names per category from all users' orders in the room
    const orderSuggestions = React.useMemo(() => {
        const categories = ['frieten', 'snacks', 'broodjes', 'dranken', 'andere'];
        const suggestions = {};
        for (const cat of categories) {
            suggestions[cat] = Array.from(new Set(
                records.flatMap(r => (r.order?.[cat] || []).map(i => i.naam).filter(Boolean))
            ));
        }
        return suggestions;
    }, [records]);

    // Category color mapping for buttons
    const categoryColors = {
        frieten: {
            bg: 'bg-yellow-100',
            text: 'text-yellow-700',
            border: 'border-yellow-600',
            hover: 'hover:bg-yellow-200',
        },
        snacks: {
            bg: 'bg-orange-100',
            text: 'text-orange-700',
            border: 'border-orange-600',
            hover: 'hover:bg-orange-200',
        },
        broodjes: {
            bg: 'bg-amber-100',
            text: 'text-amber-700',
            border: 'border-amber-600',
            hover: 'hover:bg-amber-200',
        },
        dranken: {
            bg: 'bg-blue-100',
            text: 'text-blue-700',
            border: 'border-blue-600',
            hover: 'hover:bg-blue-200',
        },
        andere: {
            bg: 'bg-gray-100',
            text: 'text-gray-700',
            border: 'border-gray-600',
            hover: 'hover:bg-gray-200',
        },
    };

    // Refs for scrolling to user orders
    const userOrderRefs = useRef({});
    // Scroll to user order by username
    const scrollToUserOrder = username => {
        const ref = userOrderRefs.current[username];
        if (ref && ref.scrollIntoView) {
            ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    // Filter state for per-user view
    const [filteredUser, setFilteredUser] = useState(null);

    // Helper: sort members with self first, then alphabetically
    const sortedRoomMembers = React.useMemo(() => {
        if (!roomMembers || !username) return roomMembers;
        const self = roomMembers.find(m => m.username === username);
        const others = roomMembers.filter(m => m.username !== username).sort((a, b) => a.username?.localeCompare(b.username));
        return self ? [self, ...others] : others;
    }, [roomMembers, username]);

    // Helper: sort records (orders) with self first, then alphabetically
    const sortedRecords = React.useMemo(() => {
        if (!records || !username) return records;
        const selfOrder = records.find(r => r.title === username);
        const others = records.filter(r => r.title !== username).sort((a, b) => a.title?.localeCompare(b.title));
        return selfOrder ? [selfOrder, ...others] : others;
    }, [records, username]);

    // Top-level loading/error screen only here
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                <Loader className="w-8 h-8 mb-2 animate-spin"/>
                <span>Laden…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-40 text-red-600">
                <AlertCircle className="w-8 h-8 mb-2"/>
                <span className="text-sm">Error: {error}</span>
            </div>
        );
    }

    if (showNameInput) {
        return (
            <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow mt-8">
                <div className="text-center mb-6">
                    <Users className="h-12 w-12 text-green-600 mx-auto mb-4"/>
                    <h1 className="text-2xl font-bold text-gray-800">Welkom bij de gezamenlijke room!</h1>
                    <p className="text-gray-600 mt-2">Voer je naam in om deel te nemen</p>
                </div>
                <form onSubmit={handleJoinRoom} className="space-y-4">
                    <input
                        type="text"
                        value={nameInput}
                        onChange={e => setNameInput(e.target.value)}
                        placeholder="Je naam..."
                        className="w-full px-4 py-3 border-2 border-green-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-200"
                        required
                        autoFocus
                    />
                    <button
                        type="submit"
                        className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 mx-auto cursor-pointer w-full justify-center"
                    >
                        <UserPlus size={18}/>
                        Deelnemen aan room
                    </button>
                </form>
                {error && <div className="text-red-600 mt-4">{error}</div>}
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto p-4 relative">
            <div className="p-4 mb-4 bg-white rounded-md shadow border border-gray-100 flex flex-col gap-1 relative ">
                {/* Subtle, compact header with room ID and share/copy */}
                <div className="flex items-center justify-between mb-2">

                    <div className="flex items-start flex-col gap-2">
                        <span className="text-xs text-gray-400 font-medium">Groep ID: {roomId}</span>
                        <button
                            className="text-xs text-red-500 hover:underline flex items-center gap-1 bg-transparent border-0 shadow-none p-0 m-0"
                            onClick={() => setShowLeaveModal(true)}
                            type="button"
                            tabIndex={0}
                        >
                            <LogOut size={14}/> Verlaten
                        </button>
                    </div>
                    {/* Leave group link in bottom right */}

                    <div className="flex flex-col gap-1 items-start justify-end">
                        <div className="flex items-center gap-2">
                            <button
                                className="px-3 py-1 text-sm bg-blue-50 text-blue-500 border-2 border-blue-200 rounded-md hover:bg-blue-100 transition-colors flex items-center gap-2 cursor-pointer shadow-none"
                            onClick={handleShare}
                        >
                            <Share2 className="w-4 h-4"/> Deel
                        </button>
                        <button
                            className="px-3 py-1 text-sm bg-blue-50 text-blue-500 border-2 border-blue-200 rounded-md hover:bg-blue-100 transition-colors flex items-center gap-2 cursor-pointer shadow-none"
                            onClick={handleCopy}
                        >
                            {copySuccess ? (
                                <>
                                    <Check className="w-4 h-4 text-blue-600"/>
                                    Gekopieerd!
                                </>
                            ) : (
                                <>
                                    <Copy className="w-4 h-4"/>
                                    Link
                                </>
                            )}
                        </button>
                        </div>
                    </div>
                </div>
                {/* Members list, lighter and compact */}
                <div className="">
                    <div className="font-semibold mb-1 text-xs text-gray-500">Deelnemers:</div>
                    <ul className="flex flex-wrap gap-2">
                        {sortedRoomMembers.map(m => (
                            <li key={m.username}>
                                <button
                                    type="button"
                                    className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 border transition-colors
                                        ${m.username === username ? 'text-yellow-700 border-yellow-600 bg-yellow-50' : 'text-gray-500 border-gray-200 bg-gray-100 hover:bg-yellow-100 hover:text-yellow-700'}
                                    `}
                                    onClick={() => {
                                        setActiveTab('peruser');
                                        setFilteredUser(m.username);
                                        scrollToUserOrder(m.username);
                                    }}
                                >
                                    <User className="w-3 h-3"/> {m.username}{m.username === username && ' (jij)'}
                                </button>
                            </li>
                        ))}
                        {filteredUser && (
                            <li>
                                <button
                                    type="button"
                                    className="px-2 py-1 rounded-full text-xs flex items-center gap-1 border border-green-600 bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                                    onClick={() => {setActiveTab('peruser');setFilteredUser(null)}}
                                >
                                    <Users className="w-3 h-3"/> Toon iedereen
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
            {/* Confirm leave modal */}
            <Dialog open={showLeaveModal} onClose={() => setShowLeaveModal(false)} className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="fixed inset-0 bg-black bg-opacity-40" aria-hidden="true" />
                <div className="relative bg-white rounded-lg shadow-lg max-w-sm w-full mx-auto p-6 flex flex-col items-center">
                    <h3 className="text-lg font-bold text-red-600 mb-2 flex items-center gap-2"><LogOut size={20}/> Groep verlaten?</h3>
                    <p className="text-gray-700 text-sm mb-4 text-center">Jouw bestellingen worden mee verwijderd bij het verlaten van de groep.</p>
                    <div className="flex gap-2 w-full justify-center">
                        <button
                            className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 cursor-pointer"
                            onClick={() => setShowLeaveModal(false)}
                            type="button"
                            disabled={leaving}
                        >
                            Annuleren
                        </button>
                        <button
                            className="px-4 py-2 text-sm bg-red-100 text-red-600 border-2 border-red-600 rounded-md hover:bg-red-200 transition-colors flex items-center gap-2 cursor-pointer"
                            onClick={handleLeaveRoom}
                            type="button"
                            disabled={leaving}
                        >
                            <LogOut size={16}/> Verlaten
                        </button>
                    </div>
                </div>
            </Dialog>
            {/* Main order button, always visible */}
            <button
                className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 mx-auto cursor-pointer w-full mb-2 justify-center"
                onClick={() => {
                    const myOrder = records.find(r => r.title === username);
                    if (myOrder) {
                        setEditingOrderId(myOrder.id);
                        setInputs({
                            frieten: myOrder.order?.frieten?.length ? myOrder.order.frieten.map(i => ({...i})) : [{
                                naam: '',
                                aantal: 1
                            }],
                            snacks: myOrder.order?.snacks?.length ? myOrder.order.snacks.map(i => ({...i})) : [{
                                naam: '',
                                aantal: 1
                            }],
                            broodjes: myOrder.order?.broodjes?.length ? myOrder.order.broodjes.map(i => ({...i})) : [{
                                naam: '',
                                aantal: 1
                            }],
                            dranken: myOrder.order?.dranken?.length ? myOrder.order.dranken.map(i => ({...i})) : [{
                                naam: '',
                                aantal: 1
                            }],
                            andere: myOrder.order?.andere?.length ? myOrder.order.andere.map(i => ({...i})) : [{
                                naam: '',
                                aantal: 1
                            }],
                        });
                    } else {
                        setEditingOrderId(null);
                        setInputs({
                            frieten: [{naam: '', aantal: 1}],
                            snacks: [{naam: '', aantal: 1}],
                            broodjes: [{naam: '', aantal: 1}],
                            dranken: [{naam: '', aantal: 1}],
                            andere: [{naam: '', aantal: 1}],
                        });
                    }
                    setShowOrderModal(true);
                }}
            >
                {records.find(r => r.title === username) ? (
                    <>
                        <Pencil className="w-4 h-4 inline-block mr-1" />
                        Mijn bestelling bewerken
                    </>
                ) : (
                    <>
                        <Plus className="w-4 h-4 inline-block mr-1" />
                        Mijn bestelling invoeren
                    </>
                )}
            </button>

            {/* Modal for friet-bestelling */}
            {showOrderModal && (
                <div
                    className="fixed inset-0 z-50 flex min-h-screen items-stretch justify-center bg-black bg-opacity-40 overflow-x-hidden">
                    <form
                        onSubmit={handleOrderSubmit}
                        className="bg-white rounded-none shadow-lg w-full h-full max-w-full flex flex-col overflow-x-hidden fixed inset-0 sm:rounded-lg sm:max-w-lg sm:my-8 sm:static sm:h-auto"
                        onClick={e => e.stopPropagation()}
                    >
                        <button type="button" onClick={() => setShowOrderModal(false)}
                                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"><X size={20}/>
                        </button>
                        {/* Modal content: scrollable order form */}
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 sm:pr-4 w-full max-w-full">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                {editingOrderId ? 'Bewerk je bestelling' : 'Vul je bestelling in'}
                            </h2>
                            <div className="pb-4 text-xs text-gray-400 font-medium">Je kan ook klikken op suggesties van de groep om snel iets toe te voegen.
                            </div>
                            {[
                                {key: 'frieten', label: 'Frieten', icon: <Popcorn className="text-yellow-500"/>},
                                {key: 'snacks', label: 'Snacks', icon: <Drumstick className="text-red-500"/>},
                                {key: 'broodjes', label: 'Broodjes', icon: <Sandwich className="text-orange-500"/>},
                                {key: 'dranken', label: 'Dranken', icon: <Beer className="text-blue-500"/>},
                                {key: 'andere', label: 'Overig', icon: <Soup className="text-purple-500"/>},
                            ].map(({key, label, icon}) => (
                                <div key={key} className="mb-2">
                                    {/*<div className="flex items-center gap-2 mb-3 font-semibold text-base">{icon} {label}</div>*/}
                                    {inputs[key].map((item, idx) => (
                                        <div key={idx} className="flex flex-col w-full mb-2">
                                            <div className="flex items-center w-full">
                      <span
                          className={`flex items-center justify-center w-10 h-10 md:w-12 md:h-10 border-2 border-r-0 rounded-l-md ${
                              {
                                  frieten: 'border-yellow-500 bg-yellow-50 text-yellow-600',
                                  snacks: 'border-red-500    bg-red-50    text-red-600',
                                  broodjes: 'border-orange-500 bg-orange-50 text-orange-600',
                                  dranken: 'border-blue-500   bg-blue-50   text-blue-600',
                                  andere: 'border-purple-500 bg-purple-50 text-purple-600',
                              }[key]
                          }`}
                      >
                        {icon}
                      </span>

                                                <input
                                                    ref={el => inputRefs.current[key][idx] = el}
                                                    className={`flex-1 min-w-0 px-3 py-2 h-10 border-2 border-r-0 focus:outline-none focus:ring-2 text-sm rounded-none
                                                        ${key === 'frieten' ? 'border-yellow-500 focus:ring-yellow-200' :
                                                            key === 'snacks' ? 'border-red-500 focus:ring-red-200' :
                                                            key === 'broodjes' ? 'border-orange-500 focus:ring-orange-200' :
                                                            key === 'dranken' ? 'border-blue-500 focus:ring-blue-200' :
                                                            'border-purple-500 focus:ring-purple-200'}
                                                    `}
                                                    placeholder={getPlaceholder(key)}
                                                    value={item.naam}
                                                    onChange={e => handleInputChange(key, idx, 'naam', e.target.value)}
                                                    autoComplete="off"
                                                />
                                                <input
                                                    type="number"
                                                    min={1}
                                                    className={`w-14 sm:w-16 px-2 h-10 border-2 border-r-0 focus:outline-none focus:ring-2 text-sm text-center rounded-none disabled:bg-gray-100
                                                        ${key === 'frieten' ? 'border-yellow-500 focus:ring-yellow-200' :
                                                            key === 'snacks' ? 'border-red-500 focus:ring-red-200' :
                                                            key === 'broodjes' ? 'border-orange-500 focus:ring-orange-200' :
                                                            key === 'dranken' ? 'border-blue-500 focus:ring-blue-200' :
                                                            'border-purple-500 focus:ring-purple-200'}
                                                    `}
                                                    value={item.naam.trim() ? (item.aantal === '' ? '' : item.aantal) : ''}
                                                    onChange={e => handleInputChange(key, idx, 'aantal', e.target.value === '' ? '' : Math.max(1, Number(e.target.value)))}
                                                    disabled={!item.naam.trim()}
                                                    placeholder={'#'}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveRow(key, idx)}
                                                    className={`w-10 h-10 md:w-10 md:h-10 border-2 border-l-2
                                                        ${key === 'frieten' ? 'border-l-yellow-500 focus:ring-yellow-200' :
                                                            key === 'snacks' ? 'border-l-red-500 focus:ring-red-200' :
                                                            key === 'broodjes' ? 'border-l-orange-500 focus:ring-orange-200' :
                                                            key === 'dranken' ? 'border-l-blue-500 focus:ring-blue-200' :
                                                            'border-l-purple-500 focus:ring-purple-200'}
                                                        rounded-r-md text-red-500 border-red-200 bg-red-50 hover:text-red-700 flex items-center justify-center`}
                                                    tabIndex={-1}
                                                >
                                                    <Trash2 size={20} className="md:w-4 md:h-4 w-5 h-5"/>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    {/* Nog meer button and suggestions: only show once per category, after all rows */}
                                    <div className="flex flex-wrap gap-2 mt-1 items-center">
                                        <button type="button" onClick={() => handleAddRow(key)}
                                            className="px-2 py-1 text-xs bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-1">
                                            <Plus size={14}/> Nog meer
                                        </button>
                                        {orderSuggestions[key].length > 0 && inputs[key].some(i => i.naam.trim() === '') && (
                                            orderSuggestions[key]
                                                .filter(suggestion => !inputs[key].some(i => i.naam.trim().toLowerCase() === suggestion.toLowerCase()))
                                                .map((suggestion, sidx) => (
                                                    <button
                                                        key={suggestion + sidx}
                                                        type="button"
                                                        className={`px-2 py-1 text-xs
                                                            ${key === 'frieten' ? 'bg-yellow-100 text-yellow-700 border-yellow-600 hover:bg-yellow-200' : ''}
                                                            ${key === 'snacks' ? 'bg-red-100 text-red-700 border-red-600 hover:bg-red-200' : ''}
                                                            ${key === 'broodjes' ? 'bg-orange-100 text-orange-700 border-orange-600 hover:bg-orange-200' : ''}
                                                            ${key === 'dranken' ? 'bg-blue-100 text-blue-700 border-blue-600 hover:bg-blue-200' : ''}
                                                            ${key === 'andere' ? 'bg-purple-100 text-purple-700 border-purple-600 hover:bg-purple-200' : ''}
                                                            border-2 rounded-md transition-colors flex items-center gap-2 cursor-pointer`}
                                                        onClick={() => {
                                                            const lastIdx = inputs[key].length - 1;
                                                            if (lastIdx >= 0) handleInputChange(key, lastIdx, 'naam', suggestion);
                                                        }}
                                                    >
                                                        <ArrowUpToLine size={14} className="inline"/> {suggestion}
                                                    </button>
                                                ))
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Fixed bestel button at bottom of modal, not scrollable */}
                        <div className="border-t border-gray-200 bg-white px-4 pb-8 pt-4 flex flex-col rounded-b-lg">
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 mx-auto cursor-pointer w-full justify-center mt-2"
                                disabled={pendingAdd}
                            >
                                {pendingAdd ? <><CircleDashed className="w-4 h-4 animate-spin"/> Toevoegen…</> : <>
                                    <Check className="w-4 h-4"/> Bevestigen</>}
                            </button>
                            {error && <div className="text-red-600 mt-2 text-center">{error}</div>}
                        </div>
                    </form>
                </div>
            )}
            {/* Tabs for combined/per-user view */}
            <div className="flex mb-4 gap-2 border-b border-gray-200">
                <button
                    className={`flex-1 px-1 sm:px-2 py-2 text-xs sm:text-sm border-b-2 transition-colors flex items-center gap-2 justify-center
                        ${activeTab === 'combined' ? 'border-yellow-600 text-yellow-700 font-semibold' : 'border-transparent text-gray-400 hover:text-yellow-600'}`}
                    onClick={() => setActiveTab('combined')}
                    type="button"
                    style={{borderRadius: 0, background: 'none', boxShadow: 'none'}}
                >
                    <Users className="w-4 h-4"/> Gemeenschappelijk
                </button>
                <button
                    className={`flex-1 px-1 sm:px-2 py-2 text-xs sm:text-sm border-b-2 transition-colors flex items-center gap-2 justify-center
                        ${activeTab === 'peruser' ? 'border-yellow-600 text-yellow-700 font-semibold' : 'border-transparent text-gray-400 hover:text-yellow-600'}`}
                    onClick={() => setActiveTab('peruser')}
                    type="button"
                    style={{borderRadius: 0, background: 'none', boxShadow: 'none'}}
                >
                    <User className="w-4 h-4"/> Per gebruiker
                </button>
            </div>
            {/* Tab content */}
            {activeTab === 'combined' ? (
                <div className="mb-6">
                    {Object.values(combinedOrders).every(section => Object.keys(section).length === 0) ? (
                        <>
                            <div className="text-gray-400 text-center py-8 flex flex-col items-center gap-2">
                                <AlertCircle className="w-6 h-6"/>
                                <span className="text-base">Nog geen bestellingen</span>
                            </div>
                        </>
                    ) : (
                        <>
                            <h2 className="text-lg font-bold mb-2 flex items-center gap-2"><Users
                                className="text-yellow-600"/>Gemeenschappelijk</h2>
                            <div
                                className="relative grid grid-cols-1 gap-2 p-4 bg-white rounded-md shadow border border-gray-100 flex flex-col gap-1">
                                <span
                                    className="absolute top-0 right-0 mt-2 mr-2 px-3 py-1 bg-yellow-50 text-yellow-700 border border-yellow-600 rounded-full text-xs z-10 flex items-center gap-1 shadow-sm">
                                    <Users className="w-3 h-3"/>
                                    Samen
                                </span>
                                {['frieten', 'snacks', 'broodjes', 'dranken', 'andere'].map(section => (
                                    Object.keys(combinedOrders[section]).length > 0 && (
                                        <div key={section} className="mb-1 border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                                            <div className="flex items-center gap-2 font-medium mb-1">
                                                {section === 'frieten' &&
                                                    <Popcorn className="w-4 h-4 text-yellow-500"/>}
                                                {section === 'snacks' && <Drumstick className="w-4 h-4 text-red-500"/>}
                                                {section === 'broodjes' &&
                                                    <Sandwich className="w-4 h-4 text-orange-500"/>}
                                                {section === 'dranken' && <Beer className="w-4 h-4 text-blue-500"/>}
                                                {section === 'andere' && <Soup className="w-4 h-4 text-purple-500"/>}
                                                <span
                                                    className="text-gray-700">{section === 'andere' ? 'Overig' : section.charAt(0).toUpperCase() + section.slice(1)}:</span>
                                            </div>
                                            <ul className="ml-6 list-disc text-sm">
                                                {Object.entries(combinedOrders[section]).map(([naam, aantal]) => {
                                                    // Find all users who ordered this item
                                                    const users = records.filter(r => (r.order[section] || []).some(i => i.naam === naam)).map(r => r.title);
                                                    return (
                                                        <li key={naam} className="flex items-center gap-2">
                                                            <span className="text-xs text-gray-500">{aantal}x</span>
                                                            <span className="font-semibold text-yellow-700">{naam}</span>
                                                            {/* Subtle user list */}
                                                            {users.length > 0 && (
                                                                <span className="flex flex-wrap gap-1 text-xs text-gray-400 ml-2 items-center">
                                      {users.map(u => (
                                          <span key={u} className="flex items-center gap-1">
                                              <User className="w-3 h-3 text-gray-300"/> {u}
                                          </span>
                                      ))}
                                    </span>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )
                                ))}
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className="mb-6">
                    {/* Show message if no per-user orders */}
                    {Object.values(combinedOrders).every(section => Object.keys(section).length === 0) ? (
                        <div className="text-gray-400 text-center py-8 flex flex-col items-center gap-2">
                            <AlertCircle className="w-6 h-6" />
                            <span className="text-base">Nog geen bestellingen</span>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
                                <User className="text-yellow-600" />
                                Per gebruiker
                            </h2>
                            <ul className="space-y-3">
                                {(filteredUser
                                    ? sortedRoomMembers.filter(m => m.username === filteredUser)
                                    : sortedRoomMembers
                                ).map(member => {
                                    const record = sortedRecords.find(r => r.title === member.username);
                                    const sections = ['frieten','snacks','broodjes','dranken','andere'];
                                    const hasSections = record && record.order && sections.some(sec => record.order[sec]?.length > 0);
                                    return (
                                        <li
                                            key={member.username}
                                            ref={el => userOrderRefs.current[member.username] = el}
                                            className="p-4 bg-white rounded-md shadow border border-gray-100 flex flex-col gap-1 relative"
                                        >
                                            {/* Username badge */}
                                            <span className="absolute top-0 right-0 mt-2 mr-2 px-3 py-1 bg-yellow-50 text-yellow-700 border border-yellow-600 rounded-full text-xs z-10 flex items-center gap-1 shadow-sm">
                                                <User className="w-3 h-3" />
                                                {member.username}
                                                {member.username === username && <span className="ml-1">(jij)</span>}
                                            </span>
                                            {hasSections ? (
                                                <div className="grid grid-cols-1 gap-2">
                                                    {sections.map(section =>
                                                        record.order[section]?.length > 0 ? (
                                                            <div key={section} className="mb-1 border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                                                                <div className="flex items-center gap-2 font-medium mb-1">
                                                                    {section === 'frieten'  && <Popcorn    className="w-4 h-4 text-yellow-500" />}
                                                                    {section === 'snacks'   && <Drumstick className="w-4 h-4 text-red-500"    />}
                                                                    {section === 'broodjes' && <Sandwich  className="w-4 h-4 text-orange-500" />}
                                                                    {section === 'dranken'  && <Beer      className="w-4 h-4 text-blue-500"   />}
                                                                    {section === 'andere'   && <Soup      className="w-4 h-4 text-purple-500"/>}
                                                                    <span className="text-gray-700">
                                                                        {(section === 'andere' ? 'Overig' : section[0].toUpperCase() + section.slice(1))}:
                                                                    </span>
                                                                </div>
                                                                <ul className="ml-6 list-disc text-sm">
                                                                    {record.order[section].map((item, idx) => (
                                                                        <li key={idx} className="flex gap-2 items-center">
                                                                            <span className="text-xs text-gray-500">{item.aantal}x</span>
                                                                            <span className="font-semibold text-yellow-700">{item.naam.charAt(0).toUpperCase() + item.naam.slice(1)}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        ) : null
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-sm">Nog geen bestellingen</span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}

                </div>
            )}
            {/* Remove demo collection add button */}
            {/* Toast notification for smooth feedback */}
            {showAddToast && (
                <div
                    className="fixed left-1/2 bottom-8 z-50 -translate-x-1/2 bg-green-600 text-white rounded-lg shadow-lg px-4 py-2 flex items-center gap-2 text-sm animate-fade-in">
                    <Check className="w-4 h-4"/>
                    Opgeslagen!
                </div>
            )}
        </div>
    );
}
